import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, 
  Plus,
  Users,
  BarChart3,
  TrendingUp,
  Target,
  UserPlus,
  Crown,
  ArrowRight,
  RefreshCw,
  Leaf,
  Shield,
  Award,
  PieChart as PieChartIcon,
  Download,
  FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-3))', 'hsl(var(--chart-5))'];

export default function Organizations() {
  const { token, user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupStats, setGroupStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });
  const [inviteOrg, setInviteOrg] = useState("");

  useEffect(() => {
    fetchGroups();
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupStats(selectedGroup.id);
    }
  }, [selectedGroup]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/organizations/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/organizations/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
      if (response.data.length > 0 && !selectedGroup) {
        setSelectedGroup(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupStats = async (groupId) => {
    try {
      const response = await axios.get(`${API}/organizations/groups/${groupId}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupStats(response.data);
    } catch (error) {
      console.error("Error fetching group stats:", error);
      setGroupStats(null);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/organizations/groups`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Groupe créé avec succès !");
      setDialogOpen(false);
      setFormData({ name: "", description: "" });
      fetchGroups();
      setSelectedGroup(response.data);
    } catch (error) {
      toast.error("Erreur lors de la création du groupe");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!selectedGroup || !inviteOrg.trim()) return;
    
    try {
      await axios.post(
        `${API}/organizations/groups/${selectedGroup.id}/invite`,
        { organization_name: inviteOrg, group_id: selectedGroup.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${inviteOrg} ajouté au groupe !`);
      setInviteDialogOpen(false);
      setInviteOrg("");
      fetchGroupStats(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'invitation");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "text-chart-1";
    if (score >= 50) return "text-chart-4";
    return "text-destructive";
  };

  const handleExportPDF = async () => {
    try {
      toast.info("Génération du rapport PDF...");
      const response = await axios.get(`${API}/organizations/dashboard/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_consolide_multiorg_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Rapport PDF téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info("Génération du rapport Excel...");
      const response = await axios.get(`${API}/objectives/organization/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_rse_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Rapport Excel téléchargé !");
    } catch (error) {
      toast.error("Erreur lors de la génération Excel");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="organizations-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Multi-Organisation
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez vos groupes d'organisations et comparez les performances RSE
            </p>
          </div>
          <div className="flex items-center gap-3">
            {groups.length > 0 && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleExportExcel}
                  data-testid="export-excel-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleExportPDF}
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full px-6" data-testid="create-group-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un groupe
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope' }}>Créer un groupe d'organisations</DialogTitle>
                <DialogDescription>
                  Regroupez plusieurs organisations pour suivre leurs performances RSE ensemble
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom du groupe</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Groupe VF ACTIONS France"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="group-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Décrivez l'objectif de ce groupe..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full" data-testid="submit-group-btn">
                    Créer le groupe
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {groups.length === 0 ? (
          /* Empty State */
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Aucun groupe d'organisations
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Créez un groupe pour regrouper plusieurs organisations et suivre leurs performances RSE de manière consolidée.
              </p>
              <Button onClick={() => setDialogOpen(true)} className="rounded-full px-6">
                <Plus className="w-4 h-4 mr-2" />
                Créer votre premier groupe
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="dashboard" data-testid="tab-dashboard">
                <PieChartIcon className="w-4 h-4 mr-2" />
                Vue Consolidée
              </TabsTrigger>
              <TabsTrigger value="groups" data-testid="tab-groups">
                <Building2 className="w-4 h-4 mr-2" />
                Mes Groupes
              </TabsTrigger>
            </TabsList>

            {/* Consolidated Dashboard Tab */}
            <TabsContent value="dashboard" className="space-y-6">
              {dashboard && (
                <>
                  {/* Global KPIs */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-foreground">{dashboard.total_groups}</p>
                            <p className="text-xs text-muted-foreground">Groupes</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-chart-3/10 flex items-center justify-center">
                            <Users className="w-6 h-6 text-chart-3" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-foreground">{dashboard.total_organizations}</p>
                            <p className="text-xs text-muted-foreground">Organisations</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-chart-1/10 flex items-center justify-center">
                            <BarChart3 className="w-6 h-6 text-chart-1" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-foreground">{dashboard.total_decisions}</p>
                            <p className="text-xs text-muted-foreground">Décisions</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-chart-4/10 flex items-center justify-center">
                            <Target className="w-6 h-6 text-chart-4" />
                          </div>
                          <div>
                            <p className={`text-3xl font-bold ${getScoreColor(dashboard.overall_avg_score)}`}>
                              {dashboard.overall_avg_score}%
                            </p>
                            <p className="text-xs text-muted-foreground">Score ESG Global</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts Row */}
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* ESG Breakdown */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                          Répartition ESG
                        </CardTitle>
                        <CardDescription>Performance par catégorie</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[250px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={[
                                  { name: 'Environnement', value: dashboard.category_breakdown.environnement || 0 },
                                  { name: 'Social', value: dashboard.category_breakdown.social || 0 },
                                  { name: 'Gouvernance', value: dashboard.category_breakdown.gouvernance || 0 }
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={5}
                                dataKey="value"
                                label={({ name, value }) => `${name}: ${value}%`}
                              >
                                {[0, 1, 2].map((index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="flex justify-center gap-6 mt-4">
                          <div className="flex items-center gap-2">
                            <Leaf className="w-4 h-4 text-chart-1" />
                            <span className="text-sm">Environnement</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-chart-3" />
                            <span className="text-sm">Social</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-chart-5" />
                            <span className="text-sm">Gouvernance</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Groups Performance */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                          Performance par Groupe
                        </CardTitle>
                        <CardDescription>Score ESG moyen</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[250px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dashboard.groups_summary} layout="vertical">
                              <XAxis type="number" domain={[0, 100]} />
                              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                              <Tooltip 
                                formatter={(value) => [`${value}%`, 'Score ESG']}
                              />
                              <Bar dataKey="avg_score" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Performers */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                        <Award className="w-5 h-5 text-chart-4" />
                        Top Performers
                      </CardTitle>
                      <CardDescription>Les organisations les plus performantes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Rang</TableHead>
                            <TableHead>Organisation</TableHead>
                            <TableHead className="text-center">Décisions</TableHead>
                            <TableHead className="text-center">Score ESG</TableHead>
                            <TableHead>Performance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dashboard.top_performers.map((org, index) => (
                            <TableRow key={org.organization}>
                              <TableCell>
                                <Badge variant={index === 0 ? "default" : "outline"} className="w-8 h-8 rounded-full flex items-center justify-center">
                                  {index + 1}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-medium">{org.organization}</span>
                                  {org.organization === user?.organization && (
                                    <Badge variant="outline" className="text-xs">Vous</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">{org.decision_count}</TableCell>
                              <TableCell className="text-center">
                                <span className={`font-semibold ${getScoreColor(org.avg_score)}`}>
                                  {org.avg_score}%
                                </span>
                              </TableCell>
                              <TableCell>
                                <Progress value={org.avg_score} className="w-24" />
                              </TableCell>
                            </TableRow>
                          ))}
                          {dashboard.top_performers.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                Aucune donnée disponible. Créez des décisions pour voir les performances.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Groups Management Tab */}
            <TabsContent value="groups" className="space-y-6">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Groups List */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    Vos groupes
                  </h2>
                  {groups.map((group) => (
                    <Card 
                      key={group.id}
                      className={`cursor-pointer card-hover ${selectedGroup?.id === group.id ? 'border-primary border-2' : ''}`}
                      onClick={() => setSelectedGroup(group)}
                      data-testid={`group-card-${group.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-medium text-foreground">{group.name}</h3>
                              <p className="text-xs text-muted-foreground">
                                {group.member_organizations.length} organisation(s)
                              </p>
                            </div>
                          </div>
                          {group.parent_organization === user?.organization && (
                            <Badge variant="outline" className="text-xs">
                              <Crown className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Group Details */}
            <div className="lg:col-span-2 space-y-6">
              {selectedGroup && (
                <>
                  {/* Group Header */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle style={{ fontFamily: 'Manrope' }}>{selectedGroup.name}</CardTitle>
                          <CardDescription>{selectedGroup.description || "Aucune description"}</CardDescription>
                        </div>
                        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" data-testid="invite-org-btn">
                              <UserPlus className="w-4 h-4 mr-2" />
                              Inviter
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Inviter une organisation</DialogTitle>
                              <DialogDescription>
                                Ajoutez une organisation au groupe "{selectedGroup.name}"
                              </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleInvite} className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="org-name">Nom de l'organisation</Label>
                                <Input
                                  id="org-name"
                                  placeholder="Nom exact de l'organisation"
                                  value={inviteOrg}
                                  onChange={(e) => setInviteOrg(e.target.value)}
                                  required
                                />
                              </div>
                              <DialogFooter>
                                <Button type="submit" className="rounded-full">
                                  Ajouter au groupe
                                </Button>
                              </DialogFooter>
                            </form>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                  </Card>

                  {/* Group Stats */}
                  {groupStats && (
                    <>
                      {/* KPIs */}
                      <div className="grid md:grid-cols-3 gap-4">
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-chart-1/10 flex items-center justify-center">
                                <BarChart3 className="w-5 h-5 text-chart-1" />
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-foreground">{groupStats.total_decisions}</p>
                                <p className="text-xs text-muted-foreground">Décisions totales</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-chart-4/10 flex items-center justify-center">
                                <Target className="w-5 h-5 text-chart-4" />
                              </div>
                              <div>
                                <p className={`text-2xl font-bold ${getScoreColor(groupStats.group_avg_score)}`}>
                                  {groupStats.group_avg_score}%
                                </p>
                                <p className="text-xs text-muted-foreground">Score ESG moyen</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Users className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="text-2xl font-bold text-foreground">{groupStats.member_count}</p>
                                <p className="text-xs text-muted-foreground">Organisations</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Organization Comparison Table */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                            Comparaison des organisations
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Organisation</TableHead>
                                <TableHead className="text-center">Décisions</TableHead>
                                <TableHead className="text-center">Score ESG</TableHead>
                                <TableHead>Performance</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {Object.entries(groupStats.organization_stats).map(([orgName, stats]) => (
                                <TableRow key={orgName}>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-4 h-4 text-muted-foreground" />
                                      <span className="font-medium">{orgName}</span>
                                      {orgName === user?.organization && (
                                        <Badge variant="outline" className="text-xs">Vous</Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center">{stats.decision_count}</TableCell>
                                  <TableCell className="text-center">
                                    <span className={`font-semibold ${getScoreColor(stats.avg_score)}`}>
                                      {stats.avg_score}%
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <Progress 
                                      value={stats.avg_score} 
                                      className="w-24"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>

                      {/* Member Organizations */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                            Membres du groupe
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {selectedGroup.member_organizations.map((org) => (
                              <Badge key={org} variant="secondary" className="px-3 py-1">
                                <Building2 className="w-3 h-3 mr-1" />
                                {org}
                                {org === selectedGroup.parent_organization && (
                                  <Crown className="w-3 h-3 ml-1 text-chart-4" />
                                )}
                              </Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
